import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import IProdukt from '../../interfaces/IProdukt';
import SelectLink from './SelectLink';
import SelectOption from './SelectOption';
import { ProductWidgetContext, IProductWidgetStateFormValues } from './ProductWidgetContext';
import Quantity from './Quantity';
import Size from './Size';
import AddToCartButton from './AddToCartButton';
import './ProductWidget.scss';
import FileUpload from './FileUpload';
import { string, object, number } from 'yup';
import * as styles from './ProductWidget.module.scss';
import classnames from 'classnames';
import TextInput from './TextInput';
import CheckOptions from './CheckOptions';
import SelectColors from './SelectColors';


export function getValidatorPerFilterTyp (filterType: string, widgetType?: string) {
    switch (filterType) {
        case "typ":
            break;

        case "menge":
            return number().typeError('Bitte eine Nummer angeben!').positive("Bitte nur positive Werte eintragen!")

        case "size":
            return object().shape({ 
                width: number().typeError('Bitte eine Nummer angeben!').positive("Bitte nur positive Werte eintragen!").required().min(5, "Bitte Breite größer als 5mm eintragen"), 
                length: number().typeError('Bitte eine Nummer angeben!').positive("Bitte nur positive Werte eintragen!").required().min(5, "Bitte Länge größer als 5mm eintragen").required()
            });

        case "files":
            return object()

        default:
            switch(widgetType) {
                case "linked":
                case "link":
                    return string().required("Bitte dieses Feld ausfüllen");

                case "check":
                case "checkbox":
                    return string();

                default:
                    return string().required("Bitte dieses Feld ausfüllen");
                    
            }

    }
}

type Props = {
    product: IProdukt; // A Product Object
    hideAnfrageButton?: boolean;
}

/**
 * If you want to add a Field:
 * 1. add value to initialValue
 * 2. add Widget to return of this function
 */
export default (props: Props) => {
    const { product, hideAnfrageButton } = props;

    const widgetState = React.useContext(ProductWidgetContext);
    let { forms, dispatch} = widgetState;

    const data = useStaticQuery(graphql`
        query {
            allProdukteHJson {
                edges {
                    node {
                        productWidgetKey
                        filters {
                            typ
                            wert
                            widget
                            widgetType
                            filter
                            defaultValue
                        }
                        name
                        url
                        produktTyp
                    }
                }
            }
        }
    `);

    // Get all products in an array with an relation to this product.
    const productsArray: Array<IProdukt> = [];
    for (const { node } of data.allProdukteHJson.edges) {
        if (node.produktTyp === product.produktTyp) {
            productsArray.push(node);
        }
    }
    
    if (!product.filters || !product.produktTyp) {
        return null;
    }

     // Generate initial values
     let namespace: string = product.productWidgetKey ? product.productWidgetKey : product.produktTyp;
     let initialValues: IProductWidgetStateFormValues = {};
     initialValues.product = {value: product, error: "", validationFunc: object().required()};

     // Set initial States for each component
     for (const filter of product.filters) {
        if (filter.widget === false) {
            continue;
        }

        switch (filter.typ) {
            case "typ":
                break;

            case "menge":
                initialValues.menge = {value: 1, error: "", validationFunc: getValidatorPerFilterTyp(filter.typ, filter.widgetType)};
                break;

            case "size":
                initialValues.size = {value: {width: null, length: null}, error: "", validationFunc: getValidatorPerFilterTyp(filter.typ, filter.widgetType)};
                break;

            case "files":
                initialValues.files = {value: {}, error: "", validationFunc: getValidatorPerFilterTyp(filter.typ, filter.widgetType)};
                break;

            default:
                const defaultValue = filter.defaultValue ? filter.defaultValue : "";
                switch(filter.widgetType) {
                    case "linked":
                    case "link":
                        initialValues[filter.typ] = {value: filter.wert, error: "", validationFunc:  getValidatorPerFilterTyp(filter.typ, filter.widgetType)};
                        break;

                    case "check":
                    case "checkbox":
                        initialValues[filter.typ] = {value: defaultValue.split("|").join(", "), error: "", validationFunc:  getValidatorPerFilterTyp(filter.typ, filter.widgetType)};
                        break;

                    default:
                        initialValues[filter.typ] = {value: defaultValue, error: "", validationFunc:  getValidatorPerFilterTyp(filter.typ, filter.widgetType)};
                        
                }
        }
     }

     
     // Set initial FormState on conponentWillMount
     React.useEffect(() => {
         dispatch({
             type: "SET_INITIAL",
             namespace,
             form: initialValues,
         });

         dispatch({
             type: "SET_WIDGET_STATE",
             namespace,
             state: "open",
         });
     }, []);
    
    const onValueChange = (value: any, name: string) => {
        try {
            forms[namespace].values[name].validationFunc.validateSync(value);
            dispatch({type: "CLEAR_ERROR", name: name, namespace,});
        } catch (e) {
            dispatch({type: "SET_ERROR", name: name, namespace, message: e.message})
        }
        dispatch({type: "SET_VALUE", name, value, namespace});
    }

    // // set product to this on mounting to overwrite product already in context store
    React.useEffect(() => {
        dispatch({type: "SET_VALUE", name: 'product', value: product, namespace});
    }, []);

    if (!forms[namespace]) {
        return null; // Wait till loading is done
    }

    // Set components for each widget component
    let widgetComponents: Array<React.ReactElement> = [];
    for (const filter of product.filters) {
        if (filter.widget === false) {
            continue;
        }

        // in case no value was set
        if (!forms[namespace].values.hasOwnProperty(filter.typ)) {
            continue;
        }

        switch (filter.typ) {
            case "typ":
            case "message":
                break;

            case "menge":
                widgetComponents.push(<React.Fragment key={filter.typ}>
                    <Quantity value={forms[namespace].values["menge"]}  onChange={(data) => onValueChange(data, "menge")}  />
                </React.Fragment>);
                break;

            case "size":
                widgetComponents.push(<React.Fragment key={filter.typ}>
                        <Size value={forms[namespace].values["size"]}  onChange={(data) => onValueChange(data, "size")} />
                    </React.Fragment>);
                break;

            case "colors":
            case "Farben":
                widgetComponents.push(<React.Fragment key={filter.typ}>
                    <SelectColors product={product} filterType={filter.typ} value={forms[namespace].values[filter.typ].value} onChange={(data) => onValueChange(data, filter.typ)} />
                </React.Fragment>);
                break;

            case "files":
                widgetComponents.push(<React.Fragment key={filter.typ}>
                        <FileWrapper open={(forms[namespace].values.files.value !== null && typeof forms[namespace].values.files.value === "object" && Object.keys(forms[namespace].values.files.value).length > 0)}>
                            <FileUpload value={forms[namespace].values.files.value} onChange={(data) => { onValueChange(data, 'files')}}  />
                        </FileWrapper>
                    </React.Fragment>);
                break;

            default:

                switch(filter.widgetType) {

                    case "text":
                        widgetComponents.push(<React.Fragment key={filter.typ}>
                            <TextInput product={product} filterType={filter.typ} value={forms[namespace].values[filter.typ]} onChange={(data) => onValueChange(data, filter.typ)} />
                        </React.Fragment>);
                        break;

                    case "linked":
                    case "link":
                        widgetComponents.push(<React.Fragment key={filter.typ}>
                            <SelectLink product={product} options={productsArray} filterType={filter.typ}  onChange={(data) => onValueChange(data, filter.typ)} />
                        </React.Fragment>)
                        break;

                    case "check":
                    case "checkbox":
                        widgetComponents.push(<React.Fragment key={filter.typ}>
                            <CheckOptions product={product} filterType={filter.typ} value={forms[namespace].values[filter.typ].value} onChange={(data) => onValueChange(data, filter.typ)} />
                        </React.Fragment>);
                        break;

                    default:
                        widgetComponents.push(<React.Fragment key={filter.typ}>
                            <SelectOption product={product} filterType={filter.typ} value={forms[namespace].values[filter.typ].value} onChange={(data) => onValueChange(data, filter.typ)} />
                        </React.Fragment>);
                        
                }

            }
        }

    
    return <div className="product-widget" id="anfragen">
    
        {widgetComponents}

        {!hideAnfrageButton && <AddToCartButton  produktTyp={namespace as IProdukt["produktTyp"]} />}
    </div>
}


const FileWrapper = (props: {children: any, open?: boolean}) => {

    const [open, setOpen] = React.useState(props.open);

    const toggleOpen = () => {
        setOpen(!open);
    }

    return <div className={classnames([(open) ? "is-open" : "",  styles.component])}>
    <span className={styles.openButton} onClick={toggleOpen}>
        Druckdatei hochladen 
        <span style={{float: "right"}}>
            <svg className={(open) ? styles.chevronOpen : styles.chevronClose} width="18px" height="10px" viewBox="0 0 9 5" version="1.1" xmlns="http://www.w3.org/2000/svg"><path d="M7.646,0.375l-3.616,3.687l-3.655,-3.687" style={{fill: "none", fillRule:"nonzero", stroke: "#1D4A68", strokeWidth: 0.75}} /></svg>
        </span>
    </span>
    
    <div className={classnames([styles.collapseBox, (open) ? "" : "is-hidden"])}>
        {props.children}
    </div>

    </div>
}